import {
  Button,
  FilterBar,
  type FilterField,
  formatDateTime,
  formatMoney,
  ListView,
  type ListViewDef,
  PageHeader,
  type Search,
  useCursorList,
  ViewTabs,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import DropdownMenu from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { useQueries, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowDownUp,
  Banknote,
  Building2,
  CircleDot,
  HandCoins,
  RadioTower,
  TriangleAlert,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { paymentTermsQuery } from "../../customers";
import { messageFor } from "../../error-copy";
import { CHANNEL, isCreditTerm, ORDER_STATUS, PAYMENT_STATUS } from "../../labels";
import { datesField, optionsOf, useFleetFields, useSavedViews, viewOf } from "../../list-filters";
import { filterSearch } from "../../list-search";
import { meQuery } from "../../session";
import { PayerFilterDialog } from "../shared/payer-filter";
import styles from "./orders.module.css";

/** Every filter the list reads from the URL (P8-57); the API checks the values. */
export const orderFilters = filterSearch({
  q: "text",
  status: "text",
  not_status: "text",
  payment: "text",
  not_payment: "text",
  channel: "text",
  not_channel: "text",
  customer_id: "text",
  business_id: "text",
  customer_type: ["personal", "business"] as const,
  zone: "text",
  not_zone: "text",
  from: "text",
  to: "text",
  total_min: "text",
  total_max: "text",
  delivery_from: "text",
  delivery_to: "text",
  driver: "text",
  not_driver: "text",
  truck: "text",
  not_truck: "text",
  fillsite: "text",
  not_fillsite: "text",
  incident: ["open"] as const,
  // postpaid orders still owing: the payment follow-up list (P8-39)
  to_collect: ["true"] as const,
  sort: ["newest", "oldest", "status"] as const,
});
type OrderSearch = ReturnType<typeof orderFilters.validate>;

/** The URL keys that are filters (not the sort): what a view is compared on. */
const FILTER_KEYS = [
  "q",
  "status",
  "not_status",
  "payment",
  "not_payment",
  "channel",
  "not_channel",
  "customer_id",
  "business_id",
  "customer_type",
  "zone",
  "not_zone",
  "from",
  "to",
  "total_min",
  "total_max",
  "delivery_from",
  "delivery_to",
  "driver",
  "not_driver",
  "truck",
  "not_truck",
  "fillsite",
  "not_fillsite",
  "incident",
  "to_collect",
];

const ICON = { size: 15, "aria-hidden": true } as const;

/** The views every staff member has: the lists they open most. The first opens when the URL has no filters. */
function builtInViews(): ListViewDef[] {
  return [
    { id: "pending", label: "Pending", search: { status: "pending,accepted" } },
    { id: "ongoing", label: "Ongoing", search: { status: "processing,partially_delivered,suspended", sort: "status" } },
    { id: "unpaid", label: "Not paid", search: { payment: "unpaid,partial,failed", not_status: "cancelled" } },
    { id: "to_collect", label: "To collect", search: { to_collect: "true" } },
    { id: "incidents", label: "Open incidents", search: { incident: "open" } },
    { id: "delivered", label: "Delivered", search: { status: "delivered" } },
    { id: "cancelled", label: "Cancelled", search: { status: "cancelled" } },
    { id: "all", label: "All orders", search: {} },
  ];
}

const summaryQuery = (search: Search) => ({
  queryKey: ["orders", "summary", search],
  staleTime: 30_000,
  queryFn: () => ok(api.GET("/v1/admin/orders/summary", { params: { query: search as never } })),
});

/** Orders (P8-07, redesigned in P8-57: `Console / Orders / Redesign`): any combination of filters, saved views. */
export function OrdersPage(): ReactNode {
  const search = useSearch({ from: "/console/orders" });
  const navigate = useNavigate({ from: "/orders" });
  const format = useCompanyFormat();
  const { data: me } = useSuspenseQuery(meQuery);
  const canPlace = me?.permissions.includes("orders.write") ?? false;
  const [picking, setPicking] = useState(false);
  const [payerName, setPayerName] = useState<string | null>(null);
  const saved = useSavedViews("orders");
  const { sort, ...filters } = search;
  // Arriving with no filters opens the first view; picking All orders afterwards stays on all.
  const landed = useRef(Object.keys(filters).length === 0);
  useEffect(() => {
    if (!landed.current) return;
    landed.current = false;
    navigate({ search: (s: OrderSearch) => ({ ...s, ...builtInViews()[0].search }), replace: true });
  }, [navigate]);

  const list = useCursorList({
    queryKey: ["orders", search],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/orders", {
        params: { query: { ...search, cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const totals = useQuery(summaryQuery(filters));
  // credit orders are billed on account: a badge, so they aren't taken for unpaid ones to chase
  const terms = useQuery(paymentTermsQuery);
  const creditTerms = new Set((terms.data ?? []).filter((t) => isCreditTerm(t.kind)).map((t) => t.id));
  const builtIn = builtInViews();
  const counts = useQueries({ queries: builtIn.map(({ search: { sort: _, ...f } }) => summaryQuery(f)) });
  const views = [...builtIn.map((v, i) => ({ ...v, count: counts[i].data?.count })), ...saved.views];
  const set = useCallback(
    (patch: Search) =>
      navigate({
        search: (s: OrderSearch) =>
          Object.fromEntries(Object.entries({ ...s, ...patch }).filter(([, v]) => v !== undefined)) as OrderSearch,
      }),
    [navigate],
  );
  const first = list.rows[0];
  const fleetFields = useFleetFields("Delivery");

  const fields: FilterField[] = [
    {
      id: "status",
      label: "Status",
      group: "Order",
      icon: <CircleDot {...ICON} />,
      kind: "options",
      param: "status",
      options: optionsOf(ORDER_STATUS),
    },
    {
      id: "payment",
      label: "Payment",
      group: "Order",
      icon: <Wallet {...ICON} />,
      kind: "options",
      param: "payment",
      options: optionsOf(PAYMENT_STATUS),
    },
    {
      id: "channel",
      label: "Placed through",
      group: "Order",
      icon: <RadioTower {...ICON} />,
      kind: "options",
      param: "channel",
      options: optionsOf(CHANNEL),
    },
    datesField("placed", "Placed", "Order"),
    {
      id: "total",
      label: "Total",
      group: "Order",
      icon: <Banknote {...ICON} />,
      kind: "amount",
      min: "total_min",
      max: "total_max",
    },
    {
      id: "to_collect",
      label: "To collect",
      group: "Order",
      icon: <HandCoins {...ICON} />,
      kind: "flag",
      param: "to_collect",
      value: "true",
    },
    {
      id: "payer",
      label: "Customer or business",
      group: "Customer",
      icon: <Users {...ICON} />,
      kind: "custom",
      params: ["customer_id", "business_id"],
      describe: (s) =>
        s.customer_id || s.business_id
          ? (payerName ?? (s.business_id ? first?.business?.name : first?.customer.name) ?? "the one chosen")
          : null,
    },
    {
      id: "personal",
      label: "Personal customers",
      group: "Customer",
      icon: <UserRound {...ICON} />,
      kind: "flag",
      param: "customer_type",
      value: "personal",
    },
    {
      id: "business",
      label: "Business customers",
      group: "Customer",
      icon: <Building2 {...ICON} />,
      kind: "flag",
      param: "customer_type",
      value: "business",
    },
    ...fleetFields,
    datesField("delivery_date", "Delivery date", "Delivery", "delivery_from", "delivery_to"),
    {
      id: "incident",
      label: "Has an open incident",
      group: "Delivery",
      icon: <TriangleAlert {...ICON} />,
      kind: "flag",
      param: "incident",
      value: "open",
    },
  ];

  const t = totals.data;
  return (
    <>
      <PageHeader
        title="Orders"
        description="Every order, from every channel. Filter by anything, and save the views you use."
        actions={canPlace ? <Button onClick={() => navigate({ to: "/orders/new" })}>New order</Button> : undefined}
      />
      <ViewTabs
        views={views}
        current={viewOf(views, filters, FILTER_KEYS)}
        onPick={(v) => navigate({ search: { sort, ...v.search } as OrderSearch })}
        onSave={(label) => saved.save(label, filters)}
        onDelete={(v) => saved.remove(v.id)}
      />
      <ListView
        caption="Orders"
        itemName={{ one: "order", other: "orders" }}
        rows={list.rows}
        rowKey={(o) => o.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={orderFilters.active(filters as OrderSearch).length > 0}
        onResetFilters={() => navigate({ search: {} })}
        empty={{
          title: "No orders yet",
          description: "Orders placed by customers, businesses or staff show here.",
        }}
        toolbar={
          <div className={styles.filters}>
            <FilterBar
              fields={fields}
              search={search}
              onChange={set}
              format={format}
              query={{ param: "q", placeholder: "Search order number, customer, phone or place" }}
              onCustom={(id) => id === "payer" && setPicking(true)}
              actions={
                <DropdownMenu
                  label={sort === "oldest" ? "Oldest first" : sort === "status" ? "Status" : "Newest first"}
                  icon={<ArrowDownUp size={15} aria-hidden />}
                  items={[
                    { label: "Newest first", onSelect: () => set({ sort: undefined }) },
                    { label: "Oldest first", onSelect: () => set({ sort: "oldest" }) },
                    { label: "Status", onSelect: () => set({ sort: "status" }) },
                  ]}
                />
              }
            />
            {t ? (
              <p className={styles.summary} aria-live="polite">
                <strong>
                  {t.count.toLocaleString("en-GB")} {t.count === 1 ? "order" : "orders"}
                </strong>
                <span>
                  Value <b>{formatMoney(t.total_amount, format)}</b>
                </span>
                {t.litres ? (
                  <span>
                    Litres <b>{t.litres.toLocaleString("en-GB")} L</b>
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
        }
        columns={[
          {
            key: "order_number",
            label: "Order",
            render: (o) => (
              <span className={styles.who}>
                <Link to="/orders/$orderId" params={{ orderId: o.id }} className={styles.link}>
                  {o.order_number}
                </Link>
                <span className={styles.muted}>{formatDateTime(o.created_at, format)}</span>
              </span>
            ),
          },
          {
            key: "customer",
            label: "Customer · place",
            render: (o) => (
              <span className={styles.who}>
                <span>{o.business?.name ?? o.customer.name}</span>
                <span className={styles.muted}>
                  {[o.business ? o.customer.name : null, o.location?.label, o.zone?.name].filter(Boolean).join(" · ") ||
                    "No place"}
                </span>
              </span>
            ),
          },
          {
            key: "litres",
            label: "Litres",
            numeric: true,
            render: (o) => (o.litres ? `${o.litres.toLocaleString("en-GB")} L` : "—"),
          },
          {
            key: "deliveries",
            label: "Deliveries",
            render: (o) =>
              o.deliveries.total ? (
                <span className={styles.who}>
                  <span className={styles.muted}>
                    {o.deliveries.delivered} of {o.deliveries.total} delivered
                  </span>
                  <span className={styles.progress} aria-hidden>
                    <span style={{ width: `${(100 * o.deliveries.delivered) / o.deliveries.total}%` }} />
                  </span>
                </span>
              ) : (
                <span className={styles.muted}>None yet</span>
              ),
          },
          { key: "total_amount", label: "Total", numeric: true, render: (o) => formatMoney(o.total_amount, format) },
          {
            key: "payment_status",
            label: "Payment",
            render: (o) => (
              <span>
                <Badge tone={PAYMENT_STATUS[o.payment_status][1]}>{PAYMENT_STATUS[o.payment_status][0]}</Badge>
                {o.postpaid ? <Badge tone="info">Postpaid</Badge> : null}
                {creditTerms.has(o.payment_term_id ?? "") ? <Badge tone="info">Credit</Badge> : null}
              </span>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (o) => <Badge tone={ORDER_STATUS[o.status][1]}>{ORDER_STATUS[o.status][0]}</Badge>,
          },
        ]}
      />
      {picking ? (
        <PayerFilterDialog
          title="Whose orders"
          description="Show only one customer's or one business's orders."
          action="Show their orders"
          onPick={(payer) => {
            setPicking(false);
            setPayerName(payer.label);
            set(
              "customer_id" in payer.billee
                ? { customer_id: payer.billee.customer_id, business_id: undefined }
                : { business_id: payer.billee.business_id, customer_id: undefined },
            );
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </>
  );
}
